<template>
  <div class="metrics-container">
    <div class="tiles-container">      
      <!-- Acceptance Rate Tile -->  
      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
          <v-card-item>
            <div class="spacing-25"></div>
            <div class="tiles-text">
              <div class="text-h6 mb-1">Acceptance Rate Average</div>
              <div class="text-caption">
                Over the last 28 days
              </div>
              <p class="text-h4">{{ acceptanceRateAverage.toFixed(2) }}%</p>
          </div>
        </v-card-item>
      </v-card>

      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Cumulative Number of Suggestions</div>
            <div class="text-caption">
              Over the last 28 days
            </div>
            <p class="text-h4">{{ cumulativeNumberSuggestions }}</p>
          </div>
        </v-card-item>
      </v-card>

      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Cumulative Number of Accepted Prompts</div>
            <div class="text-caption">
              Over the last 28 days
            </div>
            <p class="text-h4">{{ cumulativeNumberAcceptances }}</p>
          </div>
        </v-card-item>
      </v-card>

      <v-card elevation="4" color="white" variant="elevated" class="mx-auto my-3" style="width: 300px; height: 175px;">
        <v-card-item>
          <div class="tiles-text">
            <div class="spacing-10"></div>
            <div class="text-h6 mb-1">Cumulative Number of Lines of Code Accepted</div>
            <div class="text-caption">
              Over the last 28 days
            </div>
            <p class="text-h4">{{ cumulativeNumberLOCAccepted }}</p>
          </div>
        </v-card-item>
      </v-card>
    </div>

    <!-- Replace v-main with v-container -->
    <v-container class="charts-container">
      <!-- Acceptance Rate Chart -->
      <v-card class="chart-card mb-4">
        <v-card-title>Acceptance rate (%)</v-card-title>
        <div class="chart-wrapper">
          <Bar :data="acceptanceRateChartData" :options="chartOptions" />
        </div>
      </v-card>

      <!-- Suggestions and Acceptance Chart -->
      <v-card class="chart-card mb-4">
        <v-card-title>Total Suggestions Count | Total Acceptances Count</v-card-title>
        <div class="chart-wrapper">
          <Line :data="totalSuggestionsAndAcceptanceChartData" :options="chartOptions" />
        </div>
      </v-card>

      <!-- Lines Chart -->
      <v-card class="chart-card mb-4">
        <v-card-title>Total Lines Suggested | Total Lines Accepted</v-card-title>
        <div class="chart-wrapper">
          <Line :data="chartData" :options="chartOptions" />
        </div>
      </v-card>

      <!-- Active Users Chart -->
      <v-card class="chart-card mb-4">
        <v-card-title>Total Active Users</v-card-title>
        <div class="chart-wrapper">
          <Bar :data="totalActiveUsersChartData" :options="totalActiveUsersChartOptions" />
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, toRef } from 'vue';
import { Metrics } from '../model/Metrics';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

import { Line } from 'vue-chartjs'
import { Bar } from 'vue-chartjs'

ChartJS.register(
  ArcElement, 
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)


export default defineComponent({
  name: 'MetricsViewer',
  props: {
        metrics: {
            type: Object,
            required: true
        }
    },
  components: {
    Line,
    Bar
  }
  ,
  data () {
    return {
      data : {
        labels: ['VueJs', 'EmberJs', 'ReactJs', 'AngularJs'],
        datasets: [
          {
        backgroundColor: ['#41B883', '#E46651', '#00D8FF', '#DD1B16'],
        data: [40, 20, 80, 10]
        }
        ]
      },
      options : {
        responsive: true,
      maintainAspectRatio: false
      }
    }
  },
  setup(props) {

    //Tiles
    let acceptanceRateAverage = ref(0);
    let cumulativeNumberSuggestions = ref(0);
    let cumulativeNumberAcceptances = ref(0);
    let cumulativeNumberLOCAccepted = ref(0);

    //Acceptance Rate
    const acceptanceRateChartData = ref<{ labels: string[]; datasets: any[] }>({ labels: [], datasets: [] });

    //Total Suggestions Count | Total Acceptance Counts
    const totalSuggestionsAndAcceptanceChartData = ref<{ labels: string[]; datasets: any[] }>({ labels: [], datasets: [] });

    //Total Lines Suggested | Total Lines Accepted
    const chartData = ref<{ labels: string[]; datasets: any[] }>({ labels: [], datasets: [] });
    
    //Total Active Users
    const totalActiveUsersChartData = ref<{ labels: string[]; datasets: any[] }>({ labels: [], datasets: [] });  

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        datalabels: {
          display: false  // This will hide the data labels inside the chart
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            drawBorder: true,
            display: true
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    const totalActiveUsersChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        datalabels: {
          display: false  // This will hide the data labels inside the chart
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    const data = toRef(props, 'metrics').value;

    cumulativeNumberSuggestions.value = 0;
    const cumulativeSuggestionsData = data.map((m: Metrics) => {
      cumulativeNumberSuggestions.value += m.total_suggestions_count;
      return m.total_suggestions_count;
    });

    cumulativeNumberAcceptances.value = 0;
    const cumulativeAcceptancesData = data.map((m: Metrics) => {
      cumulativeNumberAcceptances.value += m.total_acceptances_count;
      return m.total_acceptances_count;
    });

    totalSuggestionsAndAcceptanceChartData.value = {
      labels: data.map((m: Metrics) => m.day),
      datasets: [
        {
          label: 'Total Suggestions',
          data: cumulativeSuggestionsData,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)'

        },
        {
          label: 'Total Acceptance',
          data: cumulativeAcceptancesData,
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          borderColor: 'rgba(153, 102, 255, 1)'
        },
        
      ]
    };

    cumulativeNumberLOCAccepted.value = 0;
    const cumulativeLOCAcceptedData = data.map((m: Metrics) => {
      const total_lines_accepted = m.total_lines_accepted;
      cumulativeNumberLOCAccepted.value += total_lines_accepted;
      return total_lines_accepted;
    });

    chartData.value = {
      labels: data.map((m: Metrics) => m.day),
      datasets: [
        {
          label: 'Total Lines Suggested',
          data: data.map((m: Metrics) => m.total_lines_suggested),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)'

        },
        {
          label: 'Total Lines Accepted',
          data: cumulativeLOCAcceptedData,
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          borderColor: 'rgba(153, 102, 255, 1)'
        }
      ]
    };
    
    const acceptanceRates = data.map((m: Metrics) => {
      const rate = m.total_lines_suggested !== 0 ? (m.total_lines_accepted / m.total_lines_suggested) * 100 : 0;
      return rate;
    });
    

    acceptanceRateChartData.value = {
      labels: data.map((m: Metrics) => m.day),
      datasets: [
        {
          type: 'line', // This makes the dataset a line in the chart
          label: 'Acceptance Rate',
          data: acceptanceRates,
          backgroundColor: 'rgba(173, 216, 230, 0.2)', // light blue
          borderColor: 'rgba(173, 216, 230, 1)', // darker blue
          fill: false // This makes the area under the line not filled
        }
      ]
    };
    
    if(cumulativeNumberSuggestions.value === 0){
      acceptanceRateAverage.value = 0;
    } else {
      acceptanceRateAverage.value = cumulativeNumberAcceptances.value / cumulativeNumberSuggestions.value * 100;
    }

    totalActiveUsersChartData.value = {
      labels: data.map((m: Metrics) => m.day),
      datasets: [
        {
          label: 'Total Active Users',
          data: data.map((m: Metrics) => m.total_active_users),
          backgroundColor: 'rgba(0, 0, 139, 0.2)', // dark blue with 20% opacity
          borderColor: 'rgba(255, 99, 132, 1)'
        }
      ]
    };

    return { totalSuggestionsAndAcceptanceChartData, chartData, 
      chartOptions, totalActiveUsersChartData, 
      totalActiveUsersChartOptions, acceptanceRateChartData, acceptanceRateAverage, cumulativeNumberSuggestions, 
      cumulativeNumberAcceptances, cumulativeNumberLOCAccepted };
  },
  
});
</script>

<style scoped>
.metrics-container {
  width: 100%;
  padding: 16px;
}

.tiles-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-bottom: 24px;
}

.charts-container {
  width: 100%;
  padding: 16px;
}

.chart-card {
  margin-bottom: 24px;
  padding: 16px;
}

.chart-wrapper {
  position: relative;
  height: 400px; /* Fixed height for charts */
  width: 100%;
  margin: 16px 0;
}

/* Existing styles */
.tiles-text {
  text-align: center;
}

.spacing-25 {
  height: 25px;
}

.spacing-10 {
  height: 10px;
}
</style>
